<template>
  <v-dialog v-model="dialog" width="412">
    <template #activator="{ props }">
      <v-btn
        variant="outlined"
        :disabled="isLoading || disabled || isAuctionInactive"
        :loading="isDraftLoading"
        color="#18A1C8"
        v-bind="props"
        >Save as draft</v-btn
      >
    </template>

    <v-card class="save-as-draft__card">
      <v-card-title class="save-as-draft__title">
        Proposal Submission Confirmation
      </v-card-title>
      <v-card-text class="save-as-draft__text">
        By clicking "Submit", your proposal will become visible to the company
        requesting the services . Please be aware, action does not finalize your
        bid. You still maintain the flexibility to modify your proposal up until
        the deadline of the offer submission phase.
        <br />
        If you choose not to make any changes, the proposal submitted now will
        be recognized as your starting bid in the auction.
        <br />
        The "Save as Draft" button allows you to save your progress without
        making your proposal visible to the company requesting the services.
      </v-card-text>
      <v-card-actions class="save-as-draft__actions">
        <v-btn
          color="primary"
          variant="text"
          :disabled="isLoading || isDraftLoading"
          @click="dialog = false"
          >Cancel</v-btn
        >
        <div class="save-as-draft__right-side">
          <v-btn
            variant="outlined"
            :disabled="isLoading || isDraftLoading || isAuctionInactive"
            :loading="isDraftLoading"
            color="#18A1C8"
            @click="onSaveAsDraft"
            >Save as draft</v-btn
          >
          <v-btn
            variant="flat"
            :disabled="isLoading || isDraftLoading || isAuctionInactive"
            :loading="isLoading"
            color="#18A1C8"
            @click="onSubmit"
            >Submit</v-btn
          >
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import useBid from '@/composables/useBid';
import { withDefaults, defineProps } from 'vue';
import { useStore } from 'vuex';
import { IAsset } from '@/types/store';
import { RAW_ASSETS_STATUSES } from '@/constants/statuses';

// #interfaces
interface IProps {
  disabled: boolean;
}

// #props
withDefaults(defineProps<IProps>(), {
  disabled: false
});

// #constants
const { getters, commit } = useStore();
const {
  onUpdateProposal,
  onCreateProposal,
  onUpdateBid,
  isLoading,
  isDraftLoading,
  isProposal,
  proposalId
} = useBid();

// #data
const dialog = ref(false);

// #computed
const asset = computed<IAsset>(() => {
  return getters['getAsset'];
});
const getStatus = computed<string>(() => {
  const { status } = asset.value ?? {};
  return status;
});
const isAuctionCancelled = computed<boolean>(() => {
  return getStatus.value === RAW_ASSETS_STATUSES.CANCELLED;
});
const isAuctionCompleted = computed<boolean>(() => {
  return getStatus.value === RAW_ASSETS_STATUSES.COMPLETED;
});
const isPaused = computed<boolean>(() => {
  // Don't show paused if auction is cancelled or completed
  if (isAuctionCancelled.value || isAuctionCompleted.value) {
    return false;
  }
  const { auction } = asset.value ?? {};
  return !!(auction as any)?.isPaused;
});
const isAuctionInactive = computed<boolean>(() => {
  return isPaused.value || isAuctionCancelled.value || isAuctionCompleted.value;
});
const getPhaseValues = computed<(string | number)[]>(() => {
  return getters['getPhaseValues'];
});

// #methods
const validatePhaseValues = (): boolean => {
  const phaseValues = getPhaseValues.value;

  if (!phaseValues || phaseValues.length === 0) {
    return false;
  }

  // Check if all phase values are greater than 0
  const allValid = phaseValues.every((value) => {
    const numValue = Number(value);
    return numValue > 0 && !isNaN(numValue);
  });

  return allValid;
};

const onSaveAsDraft = async () => {
  // Validate phase values before saving draft
  if (!validatePhaseValues()) {
    commit('errorNotification', {
      text: 'Invalid bid values',
      subtext:
        'All phase amounts must be greater than $0. Please review and update your bid values.',
      closable: true
    });
    return;
  }

  proposalId.value ? onUpdateProposal('draft') : onCreateProposal('draft');
};

const onSubmit = () => {
  // Validate phase values before submission
  if (!validatePhaseValues()) {
    commit('errorNotification', {
      text: 'Invalid bid values',
      subtext:
        'All phase amounts must be greater than $0. Please review and update your bid values.',
      closable: true
    });
    return;
  }

  if (isProposal.value) {
    return proposalId.value ? onUpdateProposal() : onCreateProposal();
  }
  onUpdateBid();
};
</script>
<style lang="scss">
@import '@/assets/style/_colors.scss';

.save-as-draft {
  &__card {
    padding: 24px !important;
  }
  &__title {
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    line-height: 32px;
    padding: 0 !important;
    margin-bottom: 8px;
  }
  &__text {
    color: $color-ui-gray-secondary-darken;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    padding: 0 !important;
    margin-bottom: 24px;
  }
  &__actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}
</style>
